import { useState, useRef, useEffect, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { QrCode, CameraOff, Check, ShoppingBag, Gift, AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * LoyaltyScannerDialog — El restaurante apunta la cámara al QR del cliente
 * o del canje. Gestiona dos flujos:
 *  - Customer QR (texto `menugenius:customer:{id}`): muestra datos del cliente,
 *    pide importe → suma puntos.
 *  - Redemption QR (JWT): valida anti-replay, descuenta puntos y marca canje.
 *
 * Props:
 *   open, onClose, restaurantId, onSuccess(result)
 */
export default function LoyaltyScannerDialog({ open, onClose, restaurantId, onSuccess }) {
  const scannerRef = useRef(null);
  const scannerContainerId = "loyalty-qr-scanner-region";
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("scan");   // scan | preview | processing | done
  const [preview, setPreview] = useState(null);  // customer info when preview mode
  const [amount, setAmount] = useState("");
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setStep("scan");
    setPreview(null);
    setAmount("");
    setResult(null);
    setError("");
  }, []);

  const stopCamera = useCallback(async () => {
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) {
          await scannerRef.current.stop();
        }
        await scannerRef.current.clear();
      } catch { /* ignore */ }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const handleScanResult = useCallback(async (qrData) => {
    await stopCamera();
    setStep("processing");
    setError("");
    try {
      const res = await api.scanLoyaltyQR(restaurantId, { qr_data: qrData });
      if (res.type === "customer" && res.status === "preview") {
        // Customer — ask for amount
        setPreview(res);
        setStep("preview");
      } else {
        setResult(res);
        setStep("done");
        onSuccess?.(res);
      }
    } catch (err) {
      setError(err.message || "No se pudo procesar el QR");
      setStep("done");
    }
  }, [restaurantId, onSuccess, stopCamera]);

  const startCamera = useCallback(async () => {
    setError("");
    setScanning(true);
    try {
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decodedText) => handleScanResult(decodedText),
        () => { /* silent: ignore per-frame errors */ },
      );
    } catch (err) {
      scannerRef.current = null;
      setScanning(false);
      setError(
        err?.message?.includes("Permission")
          ? "No tenemos permiso para usar la cámara. Habilítalo en los ajustes del navegador."
          : "No se pudo iniciar la cámara. Prueba con otro navegador o dispositivo.",
      );
    }
  }, [handleScanResult]);

  // Auto-start when dialog opens
  useEffect(() => {
    if (open && step === "scan" && !scanning && !scannerRef.current) {
      const t = setTimeout(() => { startCamera(); }, 300);
      return () => clearTimeout(t);
    }
  }, [open, step, scanning, startCamera]);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      stopCamera();
      reset();
    }
  }, [open, stopCamera, reset]);

  const confirmPurchase = async ({ visitOnly = false } = {}) => {
    if (!preview) return;
    const amt = parseFloat(amount);
    if (!visitOnly && (!amt || amt <= 0)) {
      toast.error("Introduce un importe válido");
      return;
    }
    setStep("processing");
    try {
      const res = await api.scanLoyaltyQR(restaurantId, {
        qr_data: `menugenius:customer:${preview.customer.customer_id}`,
        amount: visitOnly ? 0 : amt,
        visit_only: visitOnly,
      });
      setResult(res);
      setStep("done");
      onSuccess?.(res);
      toast.success(res.message || "Puntos añadidos");
    } catch (err) {
      setError(err.message || "No se pudo completar la operación");
      setStep("done");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="loyalty-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5" /> Escanear QR
          </DialogTitle>
        </DialogHeader>

        {/* ── Scan ── */}
        {step === "scan" && (
          <div className="space-y-3">
            <div
              id={scannerContainerId}
              className="rounded-xl overflow-hidden bg-black aspect-square w-full max-w-[320px] mx-auto"
              data-testid="loyalty-scanner-region"
            />
            {error && (
              <div className="rounded-lg bg-destructive/10 text-destructive p-3 text-sm flex items-start gap-2">
                <CameraOff className="w-4 h-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}
            <p className="text-xs text-center text-muted-foreground">
              Apunta la cámara al QR de fidelización del cliente o de un canje de recompensa.
            </p>
            {!scanning && !error && (
              <div className="flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        )}

        {/* ── Preview (customer) ── */}
        {step === "preview" && preview && (
          <div className="space-y-4" data-testid="scanner-preview">
            <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-4">
              <p className="text-xs text-emerald-700 dark:text-emerald-300 uppercase tracking-wide font-semibold mb-1">
                Cliente reconocido
              </p>
              <p className="text-lg font-bold">{preview.customer.name}</p>
              <p className="text-sm text-muted-foreground">
                {preview.customer.points || 0} pts · {preview.customer.total_visits || 0} visitas
              </p>
            </div>
            <div>
              <Label htmlFor="scanner-amount">Importe consumido (€)</Label>
              <Input
                id="scanner-amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
                data-testid="scanner-amount-input"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                {preview.points_per_euro} pto(s) por €{preview.points_per_visit > 0 && ` + ${preview.points_per_visit} por visita`}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => confirmPurchase({ visitOnly: true })} data-testid="scanner-visit-only-btn">
                Solo visita
              </Button>
              <Button onClick={() => confirmPurchase()} data-testid="scanner-confirm-btn">
                <Check className="w-4 h-4 mr-1.5" /> Sumar puntos
              </Button>
            </div>
          </div>
        )}

        {/* ── Processing ── */}
        {step === "processing" && (
          <div className="py-10 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground mt-3">Procesando…</p>
          </div>
        )}

        {/* ── Done ── */}
        {step === "done" && (
          <div className="space-y-4" data-testid="scanner-result">
            {error ? (
              <div className="rounded-xl bg-destructive/10 border border-destructive/30 p-5 text-center">
                <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-2" />
                <p className="font-semibold text-destructive">{error}</p>
              </div>
            ) : result?.type === "customer" ? (
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-6 text-center">
                <ShoppingBag className="w-10 h-10 text-emerald-700 mx-auto mb-2" />
                <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">+{result.points_earned} pts</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {result.customer?.name} · ahora tiene {result.total_points} pts
                </p>
              </div>
            ) : result?.type === "redemption" ? (
              <div className="rounded-xl bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 p-6 text-center">
                <Gift className="w-10 h-10 text-violet-600 mx-auto mb-2" />
                <p className="font-bold text-lg text-violet-700 dark:text-violet-300">{result.reward_name}</p>
                <p className="text-xs text-muted-foreground mt-2">Cupón: <span className="font-mono">{result.coupon_code}</span></p>
                <p className="text-sm mt-2">
                  {result.customer?.name} — quedan {result.customer?.points} pts
                </p>
              </div>
            ) : null}

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { reset(); }} data-testid="scanner-new-scan-btn">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Nuevo escaneo
              </Button>
              <Button className="flex-1" onClick={onClose} data-testid="scanner-close-btn">
                Terminar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
